import { NgIf } from '@angular/common';
import { Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ShowcaseFixtureData } from '@cds/betting-offer/domain-specific/showcase';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { CDNCachingConfig, SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { WidgetContext, WidgetType } from '@frontend/sports/types/components/widget';
import { ShowCasePayload } from '@frontend/sports/types/components/widget/types';
import { Observable, combineLatest, filter, map } from 'rxjs';

import { WidgetContextService } from '../widget/common/widget-context.service';
import { WidgetComponent } from '../widget/core/widget-registry.service';
import { ShowcaseEventModel, ShowcaseModuleData } from './showcase.model';
import { ShowcaseModule } from './showcase.module';
import { ShowcaseTrackingService } from './showcase.tracking.service';

@Component({
    selector: 'ms-showcase-widget',
    templateUrl: './showcase-widget.component.html',
    providers: [ShowcaseTrackingService],
    imports: [NgIf, ShowcaseModule],
})
export class ShowcaseWidgetComponent extends WidgetComponent<ShowCasePayload> {
    @HostBinding('class') className = 'showcase-widget';

    module: ShowcaseModuleData;
    showLoader = true;
    private shouldIncludePayload = false;

    constructor(
        private fixtureFactory: FixtureFactory,
        private contextService: WidgetContextService,
        private cdnCachingConfig: CDNCachingConfig,
        private sitecoreEventDetails: SitecoreEventDetailsConfig,
    ) {
        super();
        this.shouldIncludePayload = this.cdnCachingConfig.widgets.includes(WidgetType.ShowCase);
    }

    protected override onData(data: ShowCasePayload): void {
        if (!data?.fixtures?.length) {
            combineLatest([this.sitecoreEventDetails.whenReady, this.getData()])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(([_, data]) => this.renderData(data));
        } else {
            this.sitecoreEventDetails.whenReady.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => this.renderData(data));
        }
    }

    private getData(): Observable<ShowCasePayload> {
        return this.contextService.getWidget<ShowCasePayload>(this.getContext()).pipe(
            filter(({ payload }) => payload !== undefined),
            map(({ payload }) => payload as ShowCasePayload),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    override getContext(): Partial<WidgetContext> {
        const context = super.getContext();

        return !this.shouldIncludePayload ? context : { ...context, shouldIncludePayload: this.shouldIncludePayload };
    }

    private toShowcaseEventModel(showcaseFixture: ShowcaseFixtureData, index: number, payload: ShowCasePayload): ShowcaseEventModel {
        return {
            event: this.fixtureFactory.create(showcaseFixture.fixture),
            active: index === 0,
            gridGroup: payload.gridGroupPerSport?.[showcaseFixture.fixture.sport.id],
        };
    }

    renderData = (data: ShowCasePayload) => {
        if (data?.fixtures?.length) {
            this.module = {
                title: data.title,
                showSpecialContents: data.showSpecialContents,
                events: data.fixtures.map((fixture, index) => this.toShowcaseEventModel(fixture, index, data)),
            };
            this.showcaseDataLoaded();
        } else {
            this.hasData = false;
            this.setWidgetLoaded();
        }
    };

    showcaseDataLoaded(): void {
        this.hasData = true;
        this.setWidgetLoaded();
        this.logEmptyData();
    }
}
